import { create } from 'zustand';
import type { Ordre } from '@hector/shared-types';

/** Stable identity of one preview tab within the global panel. */
export type PreviewTabKey = string;

/** Token captured by an async operation before it is allowed to write a tab. */
export type PreviewRequestId = string;

export interface PreviewResource {
  url: string;
  /** Object URLs are tab-owned and must be released when the tab disappears. */
  kind: 'object-url' | 'remote-url';
}

export type DocumentPreviewOcrSource = 'ocr' | 'vision' | 'native' | null;

export interface DocumentPreviewInput {
  caseId: string;
  fileName: string;
  fileType: string;
  documentId?: string;
  /** Set when the preview is a Bibliothèque de modèles pack — enables the « Texte extrait »
   *  toggle sourced from GET /template-packs/:id/extracted-text (documentId stays undefined).
   *  Note: text loading via useDocumentPreviewOcr requires extension to handle this path. */
  templatePackId?: string;
  filePath?: string;
  /** Stable service-supplied identity for a preview without a persisted document id or path. */
  viewKey?: string;
}

export interface DocumentPreviewOcr {
  status: 'idle' | 'loading' | 'extracting' | 'ready' | 'error';
  content: string | null;
  source: DocumentPreviewOcrSource;
  error: string | null;
  requestId: PreviewRequestId | null;
}

/**
 * Discriminant of a preview tab. The panel hosts more than documents: the shell
 * (identity, order, active tab, capacity, lifecycle) is shared, the loading
 * pipelines are not. See `components/case/jurisprudence/CLAUDE.md`.
 */
export type PreviewTabKind = 'document' | 'decision';

/**
 * What every tab owns regardless of what it shows — this is all the tablist,
 * the capacity check and the close logic are ever allowed to read.
 */
export interface PreviewTabShell {
  key: PreviewTabKey;
  kind: PreviewTabKind;
  /** Tablist label. Mutable: a tab may only learn its real title after loading. */
  title: string;
  /** Tooltip used to tell two same-looking titles apart. */
  subtitle?: string;
}

export interface DocumentPreviewTab extends PreviewTabShell {
  kind: 'document';
  caseId: string;
  documentId?: string;
  /** Set when the preview is a Bibliothèque de modèles pack (see DocumentPreviewInput). */
  templatePackId?: string;
  /**
   * Storage-side name, kept apart from `title` on purpose: `title` is what we
   * display, `fileName` is what we hand to `DocumentViewerService.downloadDocument`.
   * Prettifying the label must never rename the downloaded file.
   */
  fileName: string;
  filePath?: string;
  fileType: string;
  resource: PreviewResource | null;
  status: 'loading' | 'ready' | 'error';
  error: string | null;
  loadRequestId: PreviewRequestId;
  viewMode: 'original' | 'text';
  ocr: DocumentPreviewOcr;
}

/**
 * Un arrêt du corpus JP. Volontairement maigre : le texte de la décision ne
 * transite JAMAIS par ce store — il vit dans le cache TanStack de `useDecision`,
 * partagé avec la page `/decisions/:ordre/:id`. Le store ne porte que l'identité
 * et le libellé, donc pas de `status` ici : le corps de l'onglet est seul à
 * connaître l'état de sa requête (cf. `components/case/jurisprudence/CLAUDE.md`).
 */
export interface DecisionPreviewTab extends PreviewTabShell {
  kind: 'decision';
  ordre: Ordre;
  decisionId: string;
  /**
   * Dossier depuis lequel l'arrêt a été ouvert, quand l'ouvreur le connaît.
   * Sert UNIQUEMENT à pré-sélectionner la destination dans « Enregistrer » :
   * ce n'est ni une identité (il n'entre pas dans la clé d'onglet) ni un droit
   * d'accès — le corpus JP est global, pas scopé cabinet.
   */
  caseId: string | null;
}

export type PreviewTab = DocumentPreviewTab | DecisionPreviewTab;

/** Libellé porté par un onglet JP tant que sa décision n'est pas chargée. */
export const DECISION_TAB_PENDING_TITLE = 'Arrêt…';

/**
 * Identité d'un onglet JP. Sans `caseId`, contrairement aux quatre formes de clé
 * des pièces : le même arrêt ouvert depuis deux dossiers est le même arrêt, et
 * un `caseId` sentinelle dans la clé ferait croire à un scope qui n'existe pas.
 * L'espace de clés reste disjoint de `document:` / `path:` / `view:` / `template-pack:`.
 */
export function createDecisionPreviewKey(ordre: Ordre, id: string): PreviewTabKey {
  return `decision:${ordre}:${id}`;
}

export const PREVIEW_TAB_MAX = 10;

function normalizeFilePath(filePath: string): string {
  return filePath.trim().replace(/\\/g, '/').replace(/\/+/g, '/').replace(/^\/+/, '');
}

/**
 * Derives the tab identity from persisted document identity before falling back to storage path.
 * A filename is intentionally never an identity: multiple pieces may share the same display name.
 */
export function createDocumentPreviewKey(input: DocumentPreviewInput): PreviewTabKey {
  if (input.documentId) {
    return `document:${input.caseId}:${input.documentId}`;
  }

  if (input.filePath?.trim()) {
    return `path:${input.caseId}:${normalizeFilePath(input.filePath)}`;
  }

  if (input.viewKey) {
    return `view:${input.caseId}:${input.viewKey}`;
  }

  // Template packs have no document id or file path — use the pack id as stable identity.
  if (input.templatePackId) {
    return `template-pack:${input.caseId}:${input.templatePackId}`;
  }

  throw new Error(
    `Cannot create a document preview key for case "${input.caseId}": provide documentId, filePath, templatePackId, or an explicit viewKey.`,
  );
}

function createTab(input: DocumentPreviewInput, key: PreviewTabKey, requestId: PreviewRequestId): DocumentPreviewTab {
  return {
    key,
    kind: 'document',
    title: input.fileName,
    caseId: input.caseId,
    documentId: input.documentId,
    templatePackId: input.templatePackId,
    fileName: input.fileName,
    filePath: input.filePath,
    fileType: input.fileType,
    resource: null,
    status: 'loading',
    error: null,
    loadRequestId: requestId,
    viewMode: 'original',
    ocr: {
      status: 'idle',
      content: null,
      source: null,
      error: null,
      requestId: null,
    },
  };
}

function revokeOwnedResource(resource: PreviewResource | null): void {
  if (resource?.kind === 'object-url') {
    URL.revokeObjectURL(resource.url);
  }
}

/**
 * Single place where a tab gives back what it owns. Teardown is per-kind on
 * purpose: only a document allocates anything (a blob URL). Keeping the switch
 * here means `closeTab` / `closePanel` never have to know what a tab holds.
 */
function releaseTab(tab: PreviewTab): void {
  switch (tab.kind) {
    case 'document':
      revokeOwnedResource(tab.resource);
      return;
    case 'decision':
      // Rien à rendre : l'arrêt vit dans le cache TanStack, partagé avec la page
      // `/decisions/:ordre/:id`. Le purger ici forcerait un refetch à la
      // réouverture d'un corpus pourtant immuable.
      return;
  }
}

export interface PreviewPanelStoreState {
  activeTabId: PreviewTabKey | null;
  tabOrder: PreviewTabKey[];
  tabsById: Record<PreviewTabKey, PreviewTab>;
}

/**
 * Narrows a tab to the document variant, or returns null. Every document-only
 * action goes through this: it is what makes « an OCR request can never land on
 * a non-document tab » a property of the store rather than a caller's promise.
 */
function getDocumentTab(state: PreviewPanelStoreState, key: PreviewTabKey): DocumentPreviewTab | null {
  const tab = state.tabsById[key];
  return tab?.kind === 'document' ? tab : null;
}

export interface PreviewPanelStoreActions {
  /** Returns null when a new tab is refused because the global ten-tab capacity is full. */
  openDocument: (input: DocumentPreviewInput, requestId: PreviewRequestId) => PreviewTabKey | null;
  /** Returns null when a new tab is refused because the global ten-tab capacity is full. */
  openDecision: (input: { ordre: Ordre; id: string; caseId?: string; title?: string }) => PreviewTabKey | null;
  /** Renomme un onglet déjà ouvert — un onglet peut n'apprendre son titre qu'après chargement. */
  renameTab: (key: PreviewTabKey, title: string, subtitle?: string) => void;
  activateTab: (key: PreviewTabKey) => void;
  setViewMode: (key: PreviewTabKey, viewMode: 'original' | 'text') => void;
  closeTab: (key: PreviewTabKey) => void;
  /**
   * Ferme les onglets des pièces supprimées. Le serveur efface le fichier avec la
   * ligne : un onglet resté ouvert redemanderait un fichier disparu (404).
   */
  closeDocumentTabs: (documentIds: readonly string[]) => void;
  closePanel: () => void;
  resolveLoad: (
    key: PreviewTabKey,
    requestId: PreviewRequestId,
    resource: PreviewResource,
    previewFileType: string,
  ) => void;
  rejectLoad: (key: PreviewTabKey, requestId: PreviewRequestId, error: string) => void;
  startOcr: (key: PreviewTabKey, requestId: PreviewRequestId, status?: 'loading' | 'extracting') => void;
  resolveOcr: (
    key: PreviewTabKey,
    requestId: PreviewRequestId,
    content: string | null,
    source: DocumentPreviewOcrSource,
  ) => void;
  rejectOcr: (key: PreviewTabKey, requestId: PreviewRequestId, error: string) => void;
  cancelOcr: (key: PreviewTabKey, requestId: PreviewRequestId) => void;
}

export type PreviewPanelStore = PreviewPanelStoreState & PreviewPanelStoreActions;

/** Whether the panel should render. Kept derived so tabs cannot be hidden accidentally. */
export function selectPreviewPanelIsOpen(state: PreviewPanelStoreState): boolean {
  return state.tabOrder.length > 0;
}

/**
 * Lecture publique d'un onglet PIÈCE. Le seul moyen prévu pour un consommateur
 * extérieur (le hook OCR, les tests) d'atteindre `resource`, `viewMode` ou `ocr` :
 * il rend `null` sur un onglet d'un autre type au lieu de laisser un cast masquer
 * la question. C'est le pendant public de la garde interne `getDocumentTab`.
 */
export function selectDocumentTab(
  state: PreviewPanelStoreState,
  key: PreviewTabKey,
): DocumentPreviewTab | null {
  return getDocumentTab(state, key);
}

export const usePreviewPanelStore = create<PreviewPanelStore>()((set, get) => ({
  activeTabId: null,
  tabOrder: [],
  tabsById: {},

  openDocument: (input, requestId) => {
    const key = createDocumentPreviewKey(input);
    const state = get();

    if (state.tabsById[key]) {
      set({
        activeTabId: key,
      });
      return key;
    }

    // Existing tabs remain activatable at capacity, but an eleventh identity is a no-op.
    if (state.tabOrder.length >= PREVIEW_TAB_MAX) return null;

    set({
      activeTabId: key,
      tabOrder: [...state.tabOrder, key],
      tabsById: { ...state.tabsById, [key]: createTab(input, key, requestId) },
    });
    return key;
  },

  openDecision: ({ ordre, id, caseId, title }) => {
    const key = createDecisionPreviewKey(ordre, id);
    const state = get();

    if (state.tabsById[key]) {
      set({ activeTabId: key });
      return key;
    }

    if (state.tabOrder.length >= PREVIEW_TAB_MAX) return null;

    const tab: DecisionPreviewTab = {
      key,
      kind: 'decision',
      // Libellé fourni par l'ouvreur quand il en connaît un — déjà assaini par le
      // service, qui est le point de défense contre les valeurs émises par un LLM.
      // Remplacé par le vrai titre dès que la décision est chargée (`renameTab`).
      title: title ?? DECISION_TAB_PENDING_TITLE,
      ordre,
      decisionId: id,
      caseId: caseId ?? null,
    };

    set({
      activeTabId: key,
      tabOrder: [...state.tabOrder, key],
      tabsById: { ...state.tabsById, [key]: tab },
    });
    return key;
  },

  renameTab: (key, title, subtitle) => {
    const state = get();
    const tab = state.tabsById[key];
    // Idempotent : le corps de l'onglet rejoue ce renommage à chaque render de
    // sa donnée en cache, et un `set` identique ferait boucler la tablist.
    if (!tab || (tab.title === title && tab.subtitle === subtitle)) return;

    set({
      tabsById: { ...state.tabsById, [key]: { ...tab, title, subtitle } },
    });
  },

  activateTab: (key) => {
    const state = get();
    if (state.tabsById[key]) {
      set({
        activeTabId: key,
      });
    }
  },

  setViewMode: (key, viewMode) => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab) return;

    set({
      tabsById: { ...state.tabsById, [key]: { ...tab, viewMode } },
    });
  },

  closeTab: (key) => {
    const state = get();
    const index = state.tabOrder.indexOf(key);
    const tab = state.tabsById[key];
    if (index < 0 || !tab) return;

    releaseTab(tab);
    const tabOrder = state.tabOrder.filter((tabKey) => tabKey !== key);
    const { [key]: _closedTab, ...tabsById } = state.tabsById;
    const activeTabId = state.activeTabId === key
      ? (tabOrder[index] ?? tabOrder[index - 1] ?? null)
      : state.activeTabId;

    set({
      activeTabId,
      tabOrder,
      tabsById,
    });
  },

  closeDocumentTabs: (documentIds) => {
    const deleted = new Set(documentIds);
    const { tabsById, closeTab } = get();
    for (const tab of Object.values(tabsById)) {
      if (tab.kind === 'document' && tab.documentId && deleted.has(tab.documentId)) closeTab(tab.key);
    }
  },

  closePanel: () => {
    const state = get();
    for (const tab of Object.values(state.tabsById)) {
      releaseTab(tab);
    }
    set({ activeTabId: null, tabOrder: [], tabsById: {} });
  },

  resolveLoad: (key, requestId, resource, previewFileType) => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab || tab.loadRequestId !== requestId) {
      // A stale completion still owns its blob URL until we explicitly release it.
      // Remote signed URLs are not locally allocated and must never be revoked.
      revokeOwnedResource(resource);
      return;
    }

    if (tab.resource?.url !== resource.url || tab.resource.kind !== resource.kind) {
      revokeOwnedResource(tab.resource);
    }
    set({
      tabsById: {
        ...state.tabsById,
        [key]: { ...tab, resource, fileType: previewFileType, status: 'ready', error: null },
      },
    });
  },

  rejectLoad: (key, requestId, error) => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab || tab.loadRequestId !== requestId) return;

    set({
      tabsById: {
        ...state.tabsById,
        [key]: { ...tab, status: 'error', error },
      },
    });
  },

  startOcr: (key, requestId, status = 'loading') => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab) return;

    set({
      tabsById: {
        ...state.tabsById,
        [key]: { ...tab, ocr: { ...tab.ocr, status, error: null, requestId } },
      },
    });
  },

  resolveOcr: (key, requestId, content, source) => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab || tab.ocr.requestId !== requestId) return;

    set({
      tabsById: {
        ...state.tabsById,
        [key]: {
          ...tab,
          ocr: { ...tab.ocr, status: 'ready', content, source, error: null },
        },
      },
    });
  },

  rejectOcr: (key, requestId, error) => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab || tab.ocr.requestId !== requestId) return;

    set({
      tabsById: {
        ...state.tabsById,
        [key]: { ...tab, ocr: { ...tab.ocr, status: 'error', error } },
      },
    });
  },

  cancelOcr: (key, requestId) => {
    const state = get();
    const tab = getDocumentTab(state, key);
    if (!tab || tab.ocr.requestId !== requestId) return;

    set({
      tabsById: {
        ...state.tabsById,
        [key]: {
          ...tab,
          ocr: { ...tab.ocr, status: 'idle', error: null, requestId: null },
        },
      },
    });
  },

}));
